/**
 * Conversion DÉTERMINISTE `geometry_figure` → `constrained_geometry`.
 *
 * Un `geometry_figure` est une figure STATIQUE (liste d'`objects` : points,
 * segments, droites, cercles, polygones, angles). Un `constrained_geometry` est
 * une figure INTERACTIVE pilotée par des points + des contraintes + des
 * « drawables » (le tracé). Les deux utilisent les mêmes coordonnées
 * mathématiques (y vers le haut).
 *
 * On convertit donc :
 *   - chaque objet `point`        → un `CGPoint`
 *   - chaque autre objet (trait)  → un `CGDrawable` équivalent
 *   - AUCUNE contrainte n'est inventée (`constraints: []`) — c'est à l'auteur de
 *     les ajouter ensuite dans l'éditeur pour rendre la figure « vivante ».
 *
 * Le `viewport` est calculé sur l'emprise des points (+ marge proportionnelle)
 * pour préserver le cadrage d'origine (le renderer CG force sinon l'inclusion de
 * l'origine, ce qui recadrerait la figure).
 *
 * Pur (aucun effet de bord, pas d'I/O) → testé en isolation (Vitest).
 */

import type {
  ConstrainedGeometryBlock,
  CGDrawable,
  CGPoint,
  GeometryFigureBlock,
  GeometryObject,
} from './visual-blocks';

/** Marge autour de l'emprise des points, en fraction de la plus grande étendue. */
const VIEWPORT_PADDING_RATIO = 0.1;

function computeViewport(points: CGPoint[]): ConstrainedGeometryBlock['viewport'] {
  if (points.length === 0) return { xMin: -5, xMax: 5, yMin: -5, yMax: 5 };
  const xs = points.map((p) => p.x);
  const ys = points.map((p) => p.y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const spanX = maxX - minX;
  const spanY = maxY - minY;
  // Marge uniforme sur les deux axes (le renderer letterboxe le reste).
  const pad = Math.max(spanX, spanY) * VIEWPORT_PADDING_RATIO || 1;
  return { xMin: minX - pad, xMax: maxX + pad, yMin: minY - pad, yMax: maxY + pad };
}

/** Mappe un objet « trait » de geometry_figure vers un drawable CG (ou null). */
function objectToDrawable(obj: GeometryObject): CGDrawable | null {
  switch (obj.type) {
    case 'point':
      return null; // géré séparément en CGPoint
    case 'segment':
      return {
        id: obj.id,
        kind: 'segment',
        a: obj.from,
        b: obj.to,
        ...(obj.color ? { color: obj.color } : {}),
        ...(obj.dashed ? { dashed: obj.dashed } : {}),
        ...(obj.label ? { label: obj.label } : {}),
      };
    case 'line':
      // Le drawable `line` CG n'a pas de `label` → on le laisse tomber.
      return {
        id: obj.id,
        kind: 'line',
        a: obj.through[0],
        b: obj.through[1],
        ...(obj.color ? { color: obj.color } : {}),
        ...(obj.dashed ? { dashed: obj.dashed } : {}),
      };
    case 'circle':
      // `circle` CG n'a pas de `label`.
      return {
        id: obj.id,
        kind: 'circle',
        center: obj.center,
        radius: obj.radius,
        ...(obj.color ? { color: obj.color } : {}),
      };
    case 'polygon':
      // `polygon` CG n'a pas de `label`.
      return {
        id: obj.id,
        kind: 'polygon',
        points: obj.points,
        ...(obj.fill ? { fill: obj.fill } : {}),
        ...(obj.stroke ? { stroke: obj.stroke } : {}),
      };
    case 'angle':
      // points = [sommet, côté1, côté2] → { vertex, a, b }
      return {
        id: obj.id,
        kind: 'angle',
        vertex: obj.points[0],
        a: obj.points[1],
        b: obj.points[2],
        ...(obj.label ? { label: obj.label } : {}),
        ...(obj.color ? { color: obj.color } : {}),
      };
    default:
      return null;
  }
}

/**
 * Convertit une figure `geometry_figure` en `constrained_geometry` équivalente
 * (mêmes points, mêmes tracés, sans contrainte). Conserve `id`, `order`,
 * `title`, `caption` du bloc source.
 */
export function geometryFigureToConstrained(block: GeometryFigureBlock): ConstrainedGeometryBlock {
  const points: CGPoint[] = block.objects
    .filter((o): o is Extract<GeometryObject, { type: 'point' }> => o.type === 'point')
    .map((p) => ({
      id: p.id,
      ...(p.name ? { name: p.name } : {}),
      x: p.x,
      y: p.y,
      ...(p.color ? { color: p.color } : {}),
    }));

  const drawables: CGDrawable[] = block.objects
    .map(objectToDrawable)
    .filter((d): d is CGDrawable => d !== null);

  return {
    id: block.id,
    type: 'constrained_geometry',
    order: block.order,
    ...(block.title ? { title: block.title } : {}),
    ...(block.caption ? { caption: block.caption } : {}),
    points,
    constraints: [],
    drawables,
    viewport: computeViewport(points),
    interactive: true,
  };
}
